import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { Button, Popover, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { describeFilterValues } from "@entrilia/shared/artifactRuntime/filterValueText";
import { getUserLocaleCode } from "@entrilia/shared/utilities/userLocaleConfig";
import type { SmartViewArtifactFilter } from "../../../../../api/types/smartViewArtifactsTypes";
import DateFilterEditor from "./DateFilterEditor";
import ValueFilterEditor from "./ValueFilterEditor";

interface Props {
  filters: SmartViewArtifactFilter[];
  values: Record<string, string[]>;
  labels: Record<string, Record<string, string>>;
  onChange: (key: string, values: string[], pickedLabels: Record<string, string>) => void;
  onClear: (key: string) => void;
}

const getFilterCaption = (filter: SmartViewArtifactFilter, values: string[], labels: Record<string, string>): string =>
  describeFilterValues(filter, values, labels, getUserLocaleCode(), new Date());

interface FilterButtonProps {
  filter: SmartViewArtifactFilter;
  values: string[];
  labels: Record<string, string>;
  onChange: (key: string, values: string[], pickedLabels: Record<string, string>) => void;
  onClear: (key: string) => void;
}

const FilterButton = ({ filter, values, labels, onChange, onClear }: FilterButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClose = () => setAnchorEl(null);

  const handleChange = (nextValues: string[], pickedLabels: Record<string, string> = {}) =>
    onChange(filter.key, nextValues, pickedLabels);

  const isNarrowed = filter.kind === "Value" && values.length > 0;

  return (
    <>
      <Button
        variant="outlined"
        color={isNarrowed ? "primary" : "secondary"}
        startIcon={filter.kind === "Value" ? <FilterListRoundedIcon /> : <EventRoundedIcon />}
        endIcon={<ExpandMoreRoundedIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ maxWidth: "24rem" }}
      >
        <Typography component="span" noWrap>
          <Typography component="span" color="textSecondary">{`${filter.label}:`}</Typography>{" "}
          <Typography component="span" color="textPrimary" sx={{ fontWeight: 500 }}>
            {getFilterCaption(filter, values, labels)}
          </Typography>
        </Typography>
      </Button>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {filter.kind === "Value" ? (
          <ValueFilterEditor
            filter={filter}
            values={values}
            labels={labels}
            onChange={handleChange}
            onClear={() => onClear(filter.key)}
            onClose={handleClose}
          />
        ) : (
          <DateFilterEditor kind={filter.kind} values={values} onChange={handleChange} onClose={handleClose} />
        )}
      </Popover>
    </>
  );
};

const ArtifactFilterBar = ({ filters, values, labels, onChange, onClear }: Props) => {
  const barFilters = filters.filter((filter) => filter.showInFilterBar);

  if (barFilters.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", minWidth: 0 }}>
      {barFilters.map((filter) => (
        <FilterButton
          key={filter.key}
          filter={filter}
          values={values[filter.key] ?? []}
          labels={labels[filter.key] ?? {}}
          onChange={onChange}
          onClear={onClear}
        />
      ))}
    </Stack>
  );
};

export default ArtifactFilterBar;
